/**
 * Session-expiry helpers for the "force relogin every 24 hours" feature.
 * See docs/FORCE_RELOGIN_24H_PLAN.md for the full design/rationale.
 */
import type { IPublicClientApplication } from '@azure/msal-browser';
import type { useAddToast } from '@hm-group/bads-react';
import {
  LS,
  LAST_LOGIN_TIMESTAMP_KEY,
  SESSION_MAX_AGE_MS,
  COOKIE_CLEAR_PREFIXES,
  TIMELINE_CONFIG_DATA_KEY,
  TIMELINE_CONFIG_TIMESTAMP_KEY,
} from '../constants/constants';

type AddToastFn = ReturnType<typeof useAddToast>['addToast'];

/** Reads the last interactive-login timestamp, or null if missing/invalid. */
export function getLastLoginTimestamp(): number | null {
  const raw = localStorage.getItem(LAST_LOGIN_TIMESTAMP_KEY);
  const parsed = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Records a fresh interactive-login timestamp (defaults to now). */
export function setLastLoginTimestamp(timestamp: number = Date.now()): void {
  localStorage.setItem(LAST_LOGIN_TIMESTAMP_KEY, String(timestamp));
}

/** True only once 24h have elapsed since the last recorded login; missing timestamp = not expired. */
export function isSessionExpired(): boolean {
  const lastLogin = getLastLoginTimestamp();
  if (lastLogin === null) return false;
  return Date.now() - lastLogin > SESSION_MAX_AGE_MS;
}

/** Best-effort clear of non-HttpOnly cookies matching COOKIE_CLEAR_PREFIXES (HttpOnly cookies are invisible to JS). */
function clearAllowlistedCookies(): void {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const name = cookie.split('=')[0];
    if (COOKIE_CLEAR_PREFIXES.some((prefix) => name.startsWith(prefix))) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    }
  }
}

/** Clears localStorage but keeps the brand/division/section/department selection and cached timeline config. */
function clearLocalStorageExceptSelection(): void {
  const preservedKeys = [
    ...Object.values(LS).flatMap((key) => [key, `${key}Label`]),
    TIMELINE_CONFIG_DATA_KEY,
    TIMELINE_CONFIG_TIMESTAMP_KEY,
  ];
  const preserved = preservedKeys
    .map((key): [string, string] | null => {
      const value = localStorage.getItem(key);
      return value === null ? null : [key, value];
    })
    .filter((entry): entry is [string, string] => entry !== null);

  localStorage.clear();
  preserved.forEach(([key, value]) => localStorage.setItem(key, value));
}

let isForcingRelogin = false;

/**
 * Shared "session expired" action: toast, then clear localStorage (preserving the
 * brand/division/section/department selection and cached timeline config) +
 * allowlisted cookies, and hard-navigate through AAD logout so the app can't keep
 * running silently.
 * Guarded so concurrent triggers (mount / visibilitychange / interceptor) only fire once.
 */
export function forceRelogin(instance: IPublicClientApplication, addToast: AddToastFn): void {
  if (isForcingRelogin) return;
  isForcingRelogin = true;

  addToast({
    title: 'Session expired',
    subtitle: 'Please log in again.',
    type: 'warning',
    timeout: 5000,
  });

  clearLocalStorageExceptSelection();
  clearAllowlistedCookies();

  setTimeout(() => {
    instance.logoutRedirect().catch(() => {
      isForcingRelogin = false;
    });
  }, 1500);
}

/** Reset the module-level relogin guard – for tests only. */
export function _resetForTesting(): void {
  isForcingRelogin = false;
}
